import { useState, useEffect, useRef, useCallback } from 'react';

import { getEnv } from '@/lib/env';

interface UseDictationOptions {
  onTranscriptChange?: (fullTranscript: string) => void;
  language?: string;
}

interface UseDictationReturn {
  isListening: boolean;
  isSupported: boolean;
  isConnecting: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

// WebSocket message types
interface TranscriptMessage {
  type: 'transcript';
  text: string;
  isFinal: boolean;
}

interface ErrorMessage {
  type: 'error';
  error: string;
}

interface ReadyMessage {
  type: 'ready';
}

interface EndedMessage {
  type: 'ended';
}

type ServerMessage = TranscriptMessage | ErrorMessage | ReadyMessage | EndedMessage;

/**
 * Get a supported audio MIME type for MediaRecorder
 */
function getSupportedMimeType(): string | null {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return null;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000; // Process in chunks to avoid call stack limits
  const chunks: string[] = [];

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    chunks.push(String.fromCharCode.apply(null, Array.from(chunk)));
  }

  return btoa(chunks.join(''));
}

/** Connection timeout in milliseconds */
const CONNECTION_TIMEOUT_MS = 10000;

/** Reconnection configuration */
const MAX_RECONNECT_ATTEMPTS = 3;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 8000;

/**
 * Hook for real-time dictation using WebSocket + MediaRecorder
 * Provides cross-browser speech-to-text functionality via Google Cloud Speech API
 */
export const useDictation = (options?: UseDictationOptions): UseDictationReturn => {
  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onTranscriptChangeRef = useRef(options?.onTranscriptChange);
  const languageRef = useRef(options?.language || 'en-GB');
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transcriptRef = useRef('');
  const cleanupRef = useRef<(resetReconnect?: boolean) => void>(() => {});
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalStopRef = useRef(false);
  const startListeningRef = useRef<(isReconnect?: boolean) => Promise<void>>(async () => {});

  // Keep the callback ref up to date
  useEffect(() => {
    onTranscriptChangeRef.current = options?.onTranscriptChange;
  }, [options?.onTranscriptChange]);

  useEffect(() => {
    languageRef.current = options?.language || 'en-GB';
  }, [options?.language]);

  // MediaRecorder is supported in all modern browsers
  const isSupported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  // Cleanup function
  const cleanup = useCallback((resetReconnect = false) => {
    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (resetReconnect) {
      reconnectAttemptsRef.current = 0;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'stop' }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsListening(false);
    setIsConnecting(false);
    setInterimTranscript('');
  }, []);

  // Keep cleanup ref up to date
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      intentionalStopRef.current = true;
      cleanupRef.current(true);
    };
  }, []);

  // Attempt reconnection with exponential backoff
  const attemptReconnect = useCallback(() => {
    if (intentionalStopRef.current || reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setError('Connection lost. Please try again.');
      }
      reconnectAttemptsRef.current = 0;
      return;
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY_MS,
    );
    reconnectAttemptsRef.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      // Trigger a reconnect while preserving transcript
      startListeningRef.current(true);
    }, delay);
  }, []);

  const startListening = useCallback(
    async (isReconnect = false) => {
      if (isListening || isConnecting) return;

      // Reset state only for fresh starts, not reconnects
      if (!isReconnect) {
        transcriptRef.current = '';
        setTranscript('');
        reconnectAttemptsRef.current = 0;
      }
      intentionalStopRef.current = false;
      setInterimTranscript('');
      setError(null);
      setIsConnecting(true);

      // Helper to start media recorder
      const startMediaRecorder = (stream: MediaStream, ws: WebSocket) => {
        const mimeType = getSupportedMimeType();
        if (!mimeType) {
          setError('Audio recording not supported in this browser.');
          cleanupRef.current();
          return;
        }

        try {
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType,
            audioBitsPerSecond: 128000,
          });
          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = async (event) => {
            if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
              const buffer = await event.data.arrayBuffer();
              const base64 = arrayBufferToBase64(buffer);
              ws.send(JSON.stringify({ type: 'audio', data: base64 }));
            }
          };

          mediaRecorder.onerror = () => {
            setError('Recording error. Please try again.');
            cleanupRef.current();
          };

          // Start recording with timeslice for regular chunks
          mediaRecorder.start(250);
        } catch (e) {
          console.error('Failed to create MediaRecorder:', e);
          setError('Failed to start recording. Please try again.');
          cleanupRef.current();
        }
      };

      // Helper to handle transcript messages
      const handleTranscript = (message: TranscriptMessage) => {
        // Reset reconnect attempts on successful transcript
        reconnectAttemptsRef.current = 0;

        if (message.isFinal) {
          transcriptRef.current += message.text;
          setTranscript(transcriptRef.current);
          setInterimTranscript('');
          onTranscriptChangeRef.current?.(transcriptRef.current);
        } else {
          setInterimTranscript(message.text);
          const fullTranscript = transcriptRef.current + message.text;
          onTranscriptChangeRef.current?.(fullTranscript);
        }
      };

      try {
        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
          },
        });
        streamRef.current = stream;

        const token = localStorage.getItem('accessToken');
        if (!token) {
          setError('Authentication required. Please log in again.');
          cleanupRef.current();
          return;
        }

        // Determine WebSocket URL from API URL (for production) or current location (for dev)
        const apiBase = getEnv('VITE_API_URL');
        const apiOrigin = new URL(apiBase);
        const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${apiOrigin.host}/dictation-stream?token=${encodeURIComponent(token)}`;

        // Connect to WebSocket
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        // Set connection timeout
        connectionTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws && !isListening) {
            setError('Connection timeout. Server may be unreachable.');
            cleanupRef.current();
          }
        }, CONNECTION_TIMEOUT_MS);

        ws.onopen = () => {
          ws.send(
            JSON.stringify({
              type: 'start',
              config: { language: languageRef.current },
            }),
          );
        };

        ws.onmessage = (event) => {
          try {
            const message: ServerMessage = JSON.parse(event.data);

            switch (message.type) {
              case 'ready':
                // Clear connection timeout on successful ready
                if (connectionTimeoutRef.current) {
                  clearTimeout(connectionTimeoutRef.current);
                  connectionTimeoutRef.current = null;
                }
                // Reset reconnect attempts on successful connection
                reconnectAttemptsRef.current = 0;
                setIsConnecting(false);
                setIsListening(true);
                startMediaRecorder(stream, ws);
                break;

              case 'transcript':
                handleTranscript(message);
                break;

              case 'error':
                setError(message.error);
                cleanupRef.current();
                break;

              case 'ended':
                cleanupRef.current();
                break;
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        ws.onerror = () => {
          // Don't set error immediately - attempt reconnection first
          cleanupRef.current();
          attemptReconnect();
        };

        ws.onclose = (event) => {
          // Only handle if this is still the active websocket
          if (wsRef.current === ws) {
            cleanupRef.current();
            // Attempt reconnect on unexpected close (not intentional stop, not normal closure)
            if (!intentionalStopRef.current && event.code !== 1000) {
              attemptReconnect();
            }
          }
        };
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setError('Microphone access denied. Please allow access and try again.');
          } else {
            setError('Failed to start dictation. Please try again.');
          }
        }
        cleanupRef.current();
      }
    },
    [isListening, isConnecting, attemptReconnect],
  );

  // Keep startListening ref up to date for reconnection
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  const stopListening = useCallback(() => {
    intentionalStopRef.current = true;
    cleanup(true);
  }, [cleanup]);

  const resetTranscript = useCallback(() => {
    transcriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  // Wrap startListening to not expose the isReconnect parameter
  const startListeningPublic = useCallback(() => {
    startListening(false);
  }, [startListening]);

  return {
    isListening,
    isSupported,
    isConnecting,
    transcript,
    interimTranscript,
    error,
    startListening: startListeningPublic,
    stopListening,
    resetTranscript,
  };
};
